import { Integration_Auth_Names_Enum, Integration_Names_Enum } from '@main/graphql/types.generated';
import { isPublicHostname } from '@main/shared/utils';
import { z } from 'zod/v4';

import { IntegrationAuth, IntegrationConnector, IntegrationConnectorCategory } from '../../lib';
import { classifyGitlabTokenRejection } from './lib/token-rejection';

export type GitlabConnectorConfig = typeof GitlabConnector._.Config;

const gitlabInstanceUrlSchema = z
  .url({
    protocol: /^https$/,
    error: 'Use an https:// URL for your GitLab instance.',
    abort: true,
  })
  .refine((value) => isPublicHostname(new URL(value).hostname), {
    error: 'Use the public hostname of your GitLab instance, not an IP address or a local name.',
    abort: true,
  })
  .refine(
    (value) => {
      const url = new URL(value);
      // Empty query and fragment delimiters are absent from URL.search and URL.hash.
      return url.href === `${url.origin}${url.pathname}`;
    },
    { error: 'GitLab instance URLs must not include credentials, queries, or fragments.' },
  );

function instanceUrl(config: { baseURL: string }) {
  return config.baseURL.replace(/\/+$/, '');
}

export const GitlabConnector = IntegrationConnector.make({
  provider: Integration_Names_Enum.Gitlab,
  logoPath: 'images/gitlab-logo.svg',
  categories: [IntegrationConnectorCategory.DevOps],
  auths: IntegrationAuth.group([
    IntegrationAuth.oauth2Code({
      name: Integration_Auth_Names_Enum.OauthClient,
      clientId: (config) => config.gitlab.clientId,
      clientSecret: (config) => config.gitlab.clientSecret,
      authorizeUrl: () => 'https://gitlab.com/oauth/authorize',
      tokenUrl: () => 'https://gitlab.com/oauth/token',
      classifyTokenRejection: classifyGitlabTokenRejection,
      scope: 'read_api',
      pkce: true,
      configSchema: z.object({
        configured: z.boolean().default(true), // This is a marker config so that the Connector doesn't appear Disconnected
      }),
      encryptedConfigSchema: z.object({}),
      userConfigSchema: {
        configSchema: z.object({}),
        encryptedConfigSchema: z.object({}),
      },
    }),
    IntegrationAuth.oauth2Code({
      name: Integration_Auth_Names_Enum.SelfHostedOauthClient,
      clientId: (_, config) => config.clientId,
      clientSecret: (_, config) => config.clientSecret,
      authorizeUrl: (config) => `${instanceUrl(config)}/oauth/authorize`,
      tokenUrl: (config) => `${instanceUrl(config)}/oauth/token`,
      classifyTokenRejection: classifyGitlabTokenRejection,
      scope: 'read_api',
      pkce: true,
      configSchema: z.object({
        baseURL: gitlabInstanceUrlSchema,
        clientId: z.string().min(1),
      }),
      encryptedConfigSchema: z.object({
        clientSecret: z.string().min(1),
      }),
    }),
  ]),
});
